/**
 * pathwayPresets.logic.ts
 * Pure domain logic and physical engineering presets for Data Center Cable Pathways.
 * Standards: ANSI/TIA-942-C, TIA-569-D, BICSI 002-2021, and NEC.
 */

import {
  type TrayMedia,
  type TrayConstruction,
  getTrayMediaConfig,
} from './traySegregation.logic.ts';

export type PathwayPresetId =
  | 'single_overhead'
  | 'dual_split'
  | 'triple_tier'
  | 'underfloor_power'
  | 'custom';

export type PathwayAlignment = 'rear' | 'centerline';
export type SpineCollectorPosition = 'head' | 'end' | 'both' | 'none';

export interface PathwayLayerConfig {
  id: string;
  media: TrayMedia;
  construction: TrayConstruction;
  label: string;
  elevationM: number;     // Relative to rack roof (e.g. +0.3) OR absolute AFF if isAff is true
  isAff?: boolean;        // True if elevation is Above Finished Floor (AFF)
  defaultWidthM: number;
  color: string;
  dash?: number[];
  strokeWidthFactor?: number;
}

export interface PathwayPresetConfig {
  id: PathwayPresetId;
  name: string;
  description: string;
  standard: string;
  layers: PathwayLayerConfig[];
  recommendedSpinePosition: SpineCollectorPosition;
  overhangM: number;
  alignment: PathwayAlignment;
}

export interface PathwayValidationResult {
  isValid: boolean;
  violations: string[];
  warnings: string[];
}

export interface DatacenterRoomConstraints {
  rack_height_m?: number;
  ceiling_height_m?: number;
  raised_floor_m?: number;
}

export const PATHWAY_PRESETS: Record<PathwayPresetId, PathwayPresetConfig> = {
  single_overhead: {
    id: 'single_overhead',
    name: 'Единый надрядовый лоток',
    description: 'Один проволочный лоток для всех сред. Оптимально для небольших серверных и краевых узлов (Edge).',
    standard: 'TIA-569 General Cable Tray',
    layers: [
      {
        id: 'single_copper',
        media: 'copper',
        construction: 'wire_mesh',
        label: 'Проволочный лоток СКС (Wire Mesh)',
        elevationM: 0.3,
        isAff: false,
        defaultWidthM: 0.3,
        color: getTrayMediaConfig('copper').color,
        dash: getTrayMediaConfig('copper').dash,
      },
    ],
    recommendedSpinePosition: 'head',
    overhangM: 0.3,
    alignment: 'rear',
  },

  dual_split: {
    id: 'dual_split',
    name: 'Раздельный двухуровневый (Оптика / Медь)',
    description: 'Индустриальный стандарт ЦОД: оптика в верхнем закрытом коробе (+0.5м), медь в нижнем лотке (+0.3м).',
    standard: 'ANSI/TIA-942-C & BICSI 002-2021',
    layers: [
      {
        id: 'dual_fiber',
        media: 'fiber',
        construction: 'fiber_raceway',
        label: 'Оптический короб (Fiber Raceway)',
        elevationM: 0.5,
        isAff: false,
        defaultWidthM: 0.2,
        color: getTrayMediaConfig('fiber').color,
        dash: getTrayMediaConfig('fiber').dash,
      },
      {
        id: 'dual_copper',
        media: 'copper',
        construction: 'wire_mesh',
        label: 'Проволочный лоток СКС (Wire Mesh)',
        elevationM: 0.3,
        isAff: false,
        defaultWidthM: 0.3,
        color: getTrayMediaConfig('copper').color,
        dash: getTrayMediaConfig('copper').dash,
      },
    ],
    recommendedSpinePosition: 'both',
    overhangM: 0.3,
    alignment: 'rear',
  },

  triple_tier: {
    id: 'triple_tier',
    name: 'Трехуровневый (Шинопровод / Оптика / Медь)',
    description: 'Корпоративный стандарт: верхний силовой шинопровод (Busway) с клиренсом ≥300мм, средняя оптика, нижняя медь.',
    standard: 'TIA-569-D & NEC 300mm Segregation',
    layers: [
      {
        id: 'triple_power',
        media: 'power',
        construction: 'power_busway',
        label: 'Силовой шинопровод (Busway)',
        elevationM: 2.8,
        isAff: true,
        defaultWidthM: 0.2,
        color: getTrayMediaConfig('power').color,
        dash: getTrayMediaConfig('power').dash,
        strokeWidthFactor: 1.25,
      },
      {
        id: 'triple_fiber',
        media: 'fiber',
        construction: 'fiber_raceway',
        label: 'Оптический короб (Fiber Raceway)',
        elevationM: 0.5,
        isAff: false,
        defaultWidthM: 0.2,
        color: getTrayMediaConfig('fiber').color,
        dash: getTrayMediaConfig('fiber').dash,
      },
      {
        id: 'triple_copper',
        media: 'copper',
        construction: 'wire_mesh',
        label: 'Проволочный лоток СКС (Wire Mesh)',
        elevationM: 0.3,
        isAff: false,
        defaultWidthM: 0.3,
        color: getTrayMediaConfig('copper').color,
        dash: getTrayMediaConfig('copper').dash,
      },
    ],
    recommendedSpinePosition: 'both',
    overhangM: 0.3,
    alignment: 'rear',
  },

  underfloor_power: {
    id: 'underfloor_power',
    name: 'Подпол (Сила) + Overhead (СКС)',
    description: 'Силовая разводка под фальшполом, структурированные слаботочные кабели — над шкафами.',
    standard: 'TIA-942 Raised Floor Architecture',
    layers: [
      {
        id: 'uf_power',
        media: 'power',
        construction: 'underfloor',
        label: 'Подпольный силовой лоток',
        elevationM: -0.3,
        isAff: true,
        defaultWidthM: 0.3,
        color: getTrayMediaConfig('power').color,
        dash: getTrayMediaConfig('power').dash,
      },
      {
        id: 'uf_copper',
        media: 'copper',
        construction: 'wire_mesh',
        label: 'Проволочный лоток СКС (Wire Mesh)',
        elevationM: 0.3,
        isAff: false,
        defaultWidthM: 0.3,
        color: getTrayMediaConfig('copper').color,
        dash: getTrayMediaConfig('copper').dash,
      },
    ],
    recommendedSpinePosition: 'head',
    overhangM: 0.3,
    alignment: 'rear',
  },

  custom: {
    id: 'custom',
    name: 'Пользовательский (Ручная настройка)',
    description: 'Произвольная конфигурация уровней, высот подвеса, ширин и используемых кабельных сред.',
    standard: 'Custom Specification',
    layers: [
      {
        id: 'custom_layer_1',
        media: 'copper',
        construction: 'wire_mesh',
        label: 'Пользовательский лоток',
        elevationM: 0.3,
        isAff: false,
        defaultWidthM: 0.3,
        color: getTrayMediaConfig('copper').color,
        dash: getTrayMediaConfig('copper').dash,
      },
    ],
    recommendedSpinePosition: 'head',
    overhangM: 0.3,
    alignment: 'rear',
  },
};

/**
 * Returns list of all standard pathway presets.
 */
export function getAllPathwayPresets(): PathwayPresetConfig[] {
  return Object.values(PATHWAY_PRESETS);
}

/**
 * Retrieves a pathway preset by its identifier (case-insensitive with fallback).
 */
export function getPathwayPreset(id?: string | null): PathwayPresetConfig {
  const norm = (id || '').toLowerCase() as PathwayPresetId;
  return PATHWAY_PRESETS[norm] || PATHWAY_PRESETS.single_overhead;
}

/**
 * Creates a typed custom pathway preset configuration.
 */
export function createCustomPathwayPreset(
  layers: PathwayLayerConfig[],
  options?: Partial<Omit<PathwayPresetConfig, 'id' | 'layers'>>
): PathwayPresetConfig {
  return {
    id: 'custom',
    name: options?.name || 'Пользовательский (Ручная настройка)',
    description: options?.description || 'Пользовательская конфигурация кабельных трасс',
    standard: options?.standard || 'Custom Specification',
    layers: layers && layers.length > 0 ? layers : PATHWAY_PRESETS.custom.layers,
    recommendedSpinePosition: options?.recommendedSpinePosition || 'head',
    overhangM: options?.overhangM ?? 0.3,
    alignment: options?.alignment || 'rear',
  };
}

/**
 * Calculates absolute Z coordinate (in meters above finished floor) for a given layer.
 */
export function calculateLayerAbsoluteZ(layer: PathwayLayerConfig, rackHeightM: number = 2.0): number {
  const rh = Number.isFinite(rackHeightM) && rackHeightM > 0 ? rackHeightM : 2.0;
  const elev = Number.isFinite(layer?.elevationM) ? layer.elevationM : 0;

  // If explicitly AFF, or underfloor construction, or negative elevation (below floor level), treat as AFF
  if (layer?.isAff || elev < 0 || layer?.construction === 'underfloor') {
    return Math.round(elev * 100) / 100;
  }
  return Math.round((rh + elev) * 100) / 100;
}

/**
 * Validates whether a pathway preset fits within the physical datacenter room constraints.
 */
export function validatePathwayPresetSuitability(
  preset: PathwayPresetConfig,
  room: DatacenterRoomConstraints
): PathwayValidationResult {
  const violations: string[] = [];
  const warnings: string[] = [];

  if (!preset || !Array.isArray(preset.layers) || preset.layers.length === 0) {
    return {
      isValid: false,
      violations: ['Некорректная конфигурация пресета (отсутствуют ярусы)'],
      warnings: [],
    };
  }

  const rackHeight = room?.rack_height_m != null && room.rack_height_m > 0 ? room.rack_height_m : 2.0;
  const ceilingHeight = room?.ceiling_height_m != null && room.ceiling_height_m > 0 ? room.ceiling_height_m : 3.5;
  const raisedFloor = room?.raised_floor_m != null ? room.raised_floor_m : 0.0;

  // 0. Physical sanity check: ceiling cannot be lower than or equal to rack height
  if (ceilingHeight <= rackHeight) {
    violations.push(
      `Высота потолка (${ceilingHeight.toFixed(2)}м) ниже высоты шкафов (${rackHeight.toFixed(2)}м).`
    );
  }

  // 1. Check ceiling clearance & rack envelope penetration for each layer
  for (const layer of preset.layers) {
    const z = calculateLayerAbsoluteZ(layer, rackHeight);

    // Overhead tray must not penetrate rack physical envelope
    if (layer.construction !== 'underfloor' && layer.elevationM >= 0 && z > 0 && z < rackHeight - 0.05) {
      violations.push(
        `Ярус "${layer.label}" (Z=${z.toFixed(2)}м) расположен ниже крыши стойки (${rackHeight.toFixed(2)}м) и пересекает корпус шкафов.`
      );
    }

    // At least 0.10m clearance below ceiling required for suspension brackets and drops
    if (z >= ceilingHeight - 0.1) {
      violations.push(
        `Ярус "${layer.label}" (Z=${z.toFixed(2)}м) упирается в потолок зала (${ceilingHeight.toFixed(2)}м).`
      );
    } else if (z >= ceilingHeight - 0.3) {
      warnings.push(
        `Ярус "${layer.label}" расположен слишком близко к потолку (зазор менее 0.3м).`
      );
    }
  }

  // 2. Check underfloor requirements and plenum depth
  for (const layer of preset.layers) {
    const isUnderfloor = layer.construction === 'underfloor' || layer.elevationM < 0;
    if (isUnderfloor) {
      if (raisedFloor <= 0.05) {
        violations.push(
          'Для подпольной прокладки в зале должен быть сконфигурирован фальшпол (raised floor > 0.1м).'
        );
      } else {
        const trayDepth = Math.abs(layer.elevationM);
        if (trayDepth > raisedFloor) {
          violations.push(
            `Глубина подпольного лотка "${layer.label}" (${trayDepth.toFixed(2)}м) превышает высоту фальшпола (${raisedFloor.toFixed(2)}м).`
          );
        }
      }
    }
  }

  // 3. Proximity check between power and data layers (TIA-569 300mm clearance)
  const powerLayers = preset.layers.filter(l => l.media === 'power');
  const dataLayers = preset.layers.filter(l => l.media === 'copper');

  for (const p of powerLayers) {
    const pz = calculateLayerAbsoluteZ(p, rackHeight);
    for (const d of dataLayers) {
      const dz = calculateLayerAbsoluteZ(d, rackHeight);
      const gap = Math.abs(pz - dz);
      if (gap < 0.25) {
        warnings.push(
          `Зазор между силовым ярусом "${p.label}" и слаботочным "${d.label}" составляет ${gap.toFixed(2)}м (рекомендуется ≥ 0.3м по TIA-569).`
        );
      }
    }
  }

  // 4. Physical vertical collision & minimum headroom between stacked trays (TIA-569-D)
  for (let i = 0; i < preset.layers.length; i++) {
    for (let j = i + 1; j < preset.layers.length; j++) {
      const l1 = preset.layers[i];
      const l2 = preset.layers[j];

      // Only compare trays in the same vertical space (both overhead or both underfloor)
      const isUf1 = l1.construction === 'underfloor' || l1.elevationM < 0;
      const isUf2 = l2.construction === 'underfloor' || l2.elevationM < 0;

      if (isUf1 === isUf2) {
        const z1 = calculateLayerAbsoluteZ(l1, rackHeight);
        const z2 = calculateLayerAbsoluteZ(l2, rackHeight);
        const gap = Math.abs(z1 - z2);

        if (gap < 0.10) {
          violations.push(
            `Физическая коллизия: недопустимо малое расстояние между ярусами "${l1.label}" и "${l2.label}" (${gap.toFixed(2)}м < 0.10м).`
          );
        } else if (gap < 0.15) {
          warnings.push(
            `Расстояние между ярусами "${l1.label}" и "${l2.label}" (${gap.toFixed(2)}м) меньше рекомендованного TIA-569-D минимума 0.15м для укладки кабеля.`
          );
        }
      }
    }
  }

  return {
    isValid: violations.length === 0,
    violations,
    warnings,
  };
}
